/**
 * useAdminAuth - Custom hook for admin authentication
 *
 * Provides centralized authentication state and utilities for admin components.
 * Wraps the API client functions in a React hook pattern for better state management.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { getAuthToken, isAuthenticated } from '@/lib/api/client';

export interface AdminAuthState {
  isAuthenticated: boolean;
  token: string | null;
  isLoading: boolean;
  error: string | null;
}

export interface UseAdminAuthReturn extends AdminAuthState {
  checkAuth: () => Promise<boolean>;
  clearAuth: () => void;
  refreshToken: () => Promise<void>;
}

/**
 * Custom hook for admin authentication management
 *
 * @returns AdminAuthState and authentication methods
 *
 * @example
 * ```tsx
 * const { isAuthenticated, token, checkAuth, clearAuth } = useAdminAuth();
 *
 * useEffect(() => {
 *   if (!isAuthenticated) {
 *     clearAuth();
 *     navigate('/login');
 *   }
 * }, [isAuthenticated]);
 * ```
 */
export function useAdminAuth(): UseAdminAuthReturn {
  const [state, setState] = useState<AdminAuthState>({
    isAuthenticated: false,
    token: null,
    isLoading: true,
    error: null,
  });

  /**
   * Check authentication status
   * Validates token existence and format
   */
  const checkAuth = useCallback(async (): Promise<boolean> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const token = getAuthToken();
      const authenticated = isAuthenticated();

      if (!authenticated || !token) {
        setState({
          isAuthenticated: false,
          token: null,
          isLoading: false,
          error: 'Authentication required. Please log in.',
        });
        return false;
      }

      setState({
        isAuthenticated: true,
        token,
        isLoading: false,
        error: null,
      });
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Authentication check failed';
      setState({
        isAuthenticated: false,
        token: null,
        isLoading: false,
        error: errorMessage,
      });
      return false;
    }
  }, []);

  /**
   * Clear authentication state
   * Removes token from localStorage and resets state
   */
  const clearAuth = useCallback(() => {
    localStorage.removeItem('token');
    setState({
      isAuthenticated: false,
      token: null,
      isLoading: false,
      error: 'Authentication cleared. Please log in.',
    });
  }, []);

  /**
   * Refresh authentication state
   * Re-checks token validity without clearing state
   */
  const refreshToken = useCallback(async () => {
    await checkAuth();
  }, [checkAuth]);

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return {
    ...state,
    checkAuth,
    clearAuth,
    refreshToken,
  };
}

/**
 * Higher-order component pattern for protecting admin components
 *
 * @example
 * ```tsx
 * withAdminAuth(SystemHealthMonitor)
 * ```
 */
export function withAdminAuth<P extends object>(
  Component: React.ComponentType<P>
): React.ComponentType<P> {
  return function ProtectedComponent(props: P) {
    const { isAuthenticated, isLoading, error } = useAdminAuth();

    if (isLoading) {
      return (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      );
    }

    if (!isAuthenticated) {
      return (
        <div className="flex flex-col items-center justify-center p-8 space-y-4">
          <div className="text-center">
            <h3 className="text-lg font-semibold">Authentication Required</h3>
            <p className="text-sm text-muted-foreground mt-2">{error || 'Please log in to access this feature.'}</p>
          </div>
        </div>
      );
    }

    return <Component {...props} />;
  };
}
